import moment from "moment";
import getProductId from "../../../shared/utils/getProductId";
import { SaleType } from "../../utils/pagarme";

// Status de pedido cancelado/estornado (selo vermelho na tabela)
export const NON_REVENUE_STATUSES = [
  "refunded",
  "pending_refund",
  "refused",
  "cancelled",
];

export const PENDING_STATUSES = [
  "pendent",
  "pendent_second",
  "pendent_third",
  "waiting_payment",
];

// Tudo do resumo de vendas (indicadores, gráficos, canais, mais vendidos)
// conta só pedido com status Pago — pendente ainda não é venda.
export const isPaidOrder = (order) => order?.status === "paid";

const itemPrice = (p) => (p.ProductSale ? p.ProductSalePrice : p.ProductPrice);

// Lucro real = receita dos itens (preço já com desconto aplicado) - custo
// total cadastrado por produto. Some por item pra funcionar mesmo com
// produtos misturados (com e sem custo cadastrado) num mesmo pedido.
export function calcRealProfit(order, productCosts) {
  const products = order.products || [];
  if (!products.length) return null;

  let revenue = 0;
  let cost = 0;
  let knownCostCount = 0;

  products.forEach((p) => {
    revenue += (p.ProductQuantity || 1) * (itemPrice(p) || 0);
    const unitCost = productCosts[getProductId(p)];
    if (unitCost !== undefined) {
      cost += (p.ProductQuantity || 1) * unitCost;
      knownCostCount += 1;
    }
  });

  if (knownCostCount === 0) return null;

  return {
    profit: revenue - cost,
    margin: revenue > 0 ? ((revenue - cost) / revenue) * 100 : null,
    partial: knownCostCount < products.length,
  };
}

/** Indicadores do topo da tela (valores em reais), só pedidos pagos. */
export function computeKpis(orders = [], productCosts = {}) {
  const valid = orders.filter(isPaidOrder);
  const revenue = valid.reduce((sum, o) => sum + (o.amount || 0), 0) / 100;
  const items = valid.reduce(
    (sum, o) =>
      sum +
      (o.products || []).reduce((s, p) => s + (p.ProductQuantity || 1), 0),
    0,
  );

  let profit = 0;
  let hasProfit = false;
  valid.forEach((o) => {
    const r = calcRealProfit(o, productCosts);
    if (r) {
      profit += r.profit;
      hasProfit = true;
    }
  });

  return {
    revenue,
    orders: valid.length,
    averageTicket: valid.length ? revenue / valid.length : 0,
    items,
    profit: hasProfit ? profit : null,
  };
}

/**
 * Receita por dia ("daily") ou por mês ("monthly") dentro do período
 * [start, end] (YYYY-MM-DD). Preenche com zero os buckets sem venda, pra
 * linha do gráfico não "pular" dias.
 */
const MAX_DAY_POINTS = 731; // ~2 anos
const MAX_MONTH_POINTS = 120; // 10 anos

export function revenueByPeriod(
  orders = [],
  granularity = "daily",
  start,
  end,
) {
  const unit = granularity === "monthly" ? "month" : "day";
  const keyFormat = unit === "month" ? "YYYY-MM" : "YYYY-MM-DD";
  const labelFormat = unit === "month" ? "MM/YY" : "DD/MM";

  const totals = {};
  orders.filter(isPaidOrder).forEach((o) => {
    const key = moment(o.dataTransaction).format(keyFormat);
    totals[key] = (totals[key] || 0) + (o.amount || 0) / 100;
  });

  const keys = Object.keys(totals).sort();
  let first = start ? moment(start) : keys.length ? moment(keys[0]) : null;
  const last = end
    ? moment(end)
    : keys.length
      ? moment(keys[keys.length - 1])
      : null;
  if (!first || !last || !first.isValid() || !last.isValid()) return [];
  if (first.isAfter(last)) return [];

  // Trava de segurança: um período absurdo (ex: ano "0002" no meio da
  // digitação) geraria centenas de milhares de pontos e congelava a tela.
  const maxPoints = unit === "month" ? MAX_MONTH_POINTS : MAX_DAY_POINTS;
  if (last.diff(first, unit) >= maxPoints) {
    first = last.clone().subtract(maxPoints - 1, unit);
  }

  const points = [];
  const cursor = first.clone().startOf(unit);
  const stop = last.clone().startOf(unit);
  while (cursor.isSameOrBefore(stop)) {
    const key = cursor.format(keyFormat);
    points.push({
      key,
      label: cursor.format(labelFormat),
      value: totals[key] || 0,
    });
    cursor.add(1, unit);
  }
  return points;
}

/**
 * Receita por canal de venda, do maior pro menor. Acima de `maxSlices`
 * canais, o excedente vira "Outros".
 */
export function salesByChannel(orders = [], maxSlices = 4) {
  const totals = {};
  orders.filter(isPaidOrder).forEach((o) => {
    const name = SaleType(o.description);
    totals[name] = (totals[name] || 0) + (o.amount || 0) / 100;
  });

  const sorted = Object.entries(totals)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const slices =
    sorted.length > maxSlices
      ? [
          ...sorted.slice(0, maxSlices - 1),
          {
            name: "Outros",
            value: sorted.slice(maxSlices - 1).reduce((s, c) => s + c.value, 0),
          },
        ]
      : sorted;

  const total = slices.reduce((s, c) => s + c.value, 0);
  return slices.map((c) => ({
    ...c,
    percent: total > 0 ? (c.value / total) * 100 : 0,
  }));
}

/** Produtos mais vendidos (por quantidade) com a receita de cada um. */
export function topProducts(orders = [], limit = 5) {
  const map = {};
  orders.filter(isPaidOrder).forEach((o) => {
    (o.products || []).forEach((p) => {
      const id = getProductId(p) || p.ProductName;
      if (!id) return;
      const qty = p.ProductQuantity || 1;
      if (!map[id]) {
        map[id] = {
          id,
          name: p.ProductName || "Produto",
          image: p.ProductImage,
          color: p.ProductColor?.colorName,
          quantity: 0,
          revenue: 0,
        };
      }
      map[id].quantity += qty;
      map[id].revenue += qty * (itemPrice(p) || 0);
    });
  });

  return Object.values(map)
    .sort((a, b) => b.quantity - a.quantity || b.revenue - a.revenue)
    .slice(0, limit);
}

/** Tom do selo de status na tabela. */
export function statusTone(status) {
  if (status === "paid") return "paid";
  if (NON_REVENUE_STATUSES.includes(status)) return "cancelled";
  if (PENDING_STATUSES.includes(status)) return "pending";
  return "neutral";
}

// Canais presenciais (loja/balcão). "online" (manual) e pedidos sem canal
// (site) são online; presente/devolução/empréstimo não entram em nenhum.
const PRESENCIAL_CHANNELS = [
  "presencial",
  "caixa",
  "casa22_rb_presencial",
  "casa22_flag_presencial",
];
const OTHER_CHANNELS = ["gift", "returned", "borrowed"];

/** "presencial" | "online" | "outros" */
export function saleChannelGroup(order) {
  const description = order?.description;
  if (PRESENCIAL_CHANNELS.includes(description)) return "presencial";
  if (OTHER_CHANNELS.includes(description)) return "outros";
  return "online";
}

/** Filtra pelo grupo de canal; "Todos" (ou vazio) não filtra. */
export function filterByChannel(orders = [], channel) {
  if (!channel || channel === "Todos") return orders;
  return orders.filter((o) => saleChannelGroup(o) === channel);
}

/**
 * Financeiro (parcelas) só vale pra venda presencial pendente. Se o pedido
 * já tem parcelas salvas, continua editável mesmo depois de virar "Pago",
 * pra dar pra desmarcar uma parcela e voltar pra Pendente.
 */
export function canEditPayment(order) {
  if (saleChannelGroup(order) !== "presencial") return false;
  return (
    PENDING_STATUSES.includes(order.status) ||
    (Array.isArray(order.paymentSchedule) && order.paymentSchedule.length > 0)
  );
}
